// ═══════════════════════════════════════════════════════════════════════════
// WRITTN - Auth Prompt Component (Soft Login)
// ═══════════════════════════════════════════════════════════════════════════
// Premium modal for authentication - appears when guest tries to save/share
// ═══════════════════════════════════════════════════════════════════════════

import { icons } from './icons.js';
import {
    signInWithGoogle,
    signInWithEmail,
    createAccountWithEmail,
    sendPhoneVerification,
    verifyPhoneCode
} from '../services/auth.js';

let currentOverlay = null;
let currentMode = 'main'; // main, email, email-signup, phone, phone-verify
let pendingAction = null; // { type: 'save' | 'share', callback: Function }

// ─────────────────────────────────────────────────────────────────────────
// SHOW AUTH PROMPT
// ─────────────────────────────────────────────────────────────────────────

export function showAuthPrompt(action = null, actionType = 'save') {
    // Store the pending action to execute after successful login
    pendingAction = action ? { callback: action, type: actionType } : null;

    const overlay = document.createElement('div');
    overlay.className = 'auth-prompt-overlay';
    overlay.id = 'auth-prompt-overlay';

    overlay.innerHTML = renderMainView();

    document.body.appendChild(overlay);
    currentOverlay = overlay;
    currentMode = 'main';

    // Animate in
    requestAnimationFrame(() => {
        overlay.classList.add('active');
    });

    // Setup event listeners
    setupEventListeners(overlay);

    // Close on backdrop click
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
            hideAuthPrompt();
        }
    });
}

export function hideAuthPrompt() {
    if (currentOverlay) {
        currentOverlay.classList.remove('active');
        setTimeout(() => {
            currentOverlay?.remove();
            currentOverlay = null;
            currentMode = 'main';
        }, 300);
    }
}

// ─────────────────────────────────────────────────────────────────────────
// VIEW RENDERERS
// ─────────────────────────────────────────────────────────────────────────

function renderMainView() {
    const title = pendingAction?.type === 'share'
        ? 'Share this verse'
        : 'Save to your heart';

    const subtitle = pendingAction?.type === 'share'
        ? 'Sign in to share and track your shared verses'
        : 'Sign in to save verses across all your devices';

    return `
    <div class="auth-prompt-card">
      <h2 class="auth-prompt-title">${title}</h2>
      <p class="auth-prompt-subtitle">${subtitle}</p>
      
      <button class="auth-btn auth-btn-google" id="auth-google">
        ${icons.google}
        <span>Continue with Google</span>
      </button>
      
      <button class="auth-btn auth-btn-email" id="auth-email">
        ${icons.email}
        <span>Continue with Email</span>
      </button>
      
      <button class="auth-btn auth-btn-skip" id="auth-skip">
        Maybe later
      </button>
    </div>
  `;
}

function renderEmailView(isSignUp = false) {
    return `
    <div class="auth-prompt-card">
      <button class="auth-back-btn" id="auth-back">← Back</button>
      
      <h2 class="auth-prompt-title">${isSignUp ? 'Create account' : 'Sign in with email'}</h2>
      
      <form class="auth-form" id="email-form">
        ${isSignUp ? `
          <input 
            type="text" 
            class="auth-input" 
            id="auth-name" 
            placeholder="Your name"
            autocomplete="name"
          />
        ` : ''}
        
        <input 
          type="email" 
          class="auth-input" 
          id="auth-email-input" 
          placeholder="Email address"
          autocomplete="email"
          required
        />
        
        <input 
          type="password" 
          class="auth-input" 
          id="auth-password" 
          placeholder="Password"
          autocomplete="${isSignUp ? 'new-password' : 'current-password'}"
          required
          minlength="6"
        />
        
        <div class="auth-error" id="auth-error"></div>
        
        <button type="submit" class="auth-btn auth-btn-primary" id="auth-submit">
          ${isSignUp ? 'Create account' : 'Sign in'}
        </button>
      </form>
      
      <button class="auth-toggle-btn" id="auth-toggle">
        ${isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Create one"}
      </button>
    </div>
  `;
}

function renderPhoneView() {
    return `
    <div class="auth-prompt-card">
      <button class="auth-back-btn" id="auth-back">← Back</button>
      
      <h2 class="auth-prompt-title">Sign in with phone</h2>
      <p class="auth-prompt-subtitle">We'll send you a verification code</p>
      
      <form class="auth-form" id="phone-form">
        <input 
          type="tel" 
          class="auth-input" 
          id="auth-phone-input" 
          placeholder="+91 98765 43210"
          autocomplete="tel"
          required
        />
        
        <div class="auth-error" id="auth-error"></div>
        
        <button type="submit" class="auth-btn auth-btn-primary" id="auth-send-code">
          Send verification code
        </button>
      </form>
      
      <div id="recaptcha-container"></div>
    </div>
  `;
}

function renderPhoneVerifyView() {
    return `
    <div class="auth-prompt-card">
      <button class="auth-back-btn" id="auth-back">← Back</button>
      
      <h2 class="auth-prompt-title">Enter verification code</h2>
      <p class="auth-prompt-subtitle">We sent a code to your phone</p>
      
      <form class="auth-form" id="verify-form">
        <input 
          type="text" 
          class="auth-input auth-input-code" 
          id="auth-code-input" 
          placeholder="123456"
          maxlength="6"
          pattern="[0-9]{6}"
          inputmode="numeric"
          autocomplete="one-time-code"
          required
        />
        
        <div class="auth-error" id="auth-error"></div>
        
        <button type="submit" class="auth-btn auth-btn-primary" id="auth-verify">
          Verify
        </button>
      </form>
    </div>
  `;
}

// ─────────────────────────────────────────────────────────────────────────
// EVENT HANDLING
// ─────────────────────────────────────────────────────────────────────────

function setupEventListeners(overlay) {
    // Use event delegation for dynamic content
    overlay.addEventListener('click', async (e) => {
        const target = e.target.closest('button');
        if (!target) return;

        const id = target.id;

        switch (id) {
            case 'auth-google':
                handleGoogleSignIn();
                break;
            case 'auth-email':
                switchView('email');
                break;
            case 'auth-phone':
                switchView('phone');
                break;
            case 'auth-skip':
                hideAuthPrompt();
                break;
            case 'auth-back':
                switchView('main');
                break;
            case 'auth-toggle':
                switchView(currentMode === 'email' ? 'email-signup' : 'email');
                break;
        }
    });

    overlay.addEventListener('submit', async (e) => {
        e.preventDefault();

        const form = e.target;
        const formId = form.id;

        switch (formId) {
            case 'email-form':
                handleEmailSubmit(form);
                break;
            case 'phone-form':
                handlePhoneSend(form);
                break;
            case 'verify-form':
                handlePhoneVerify(form);
                break;
        }
    });
}

function switchView(view) {
    currentMode = view;
    const card = currentOverlay?.querySelector('.auth-prompt-card');
    if (!card) return;

    // Animate out
    card.style.opacity = '0';
    card.style.transform = 'translateY(20px)';

    setTimeout(() => {
        let newContent;
        switch (view) {
            case 'email':
                newContent = renderEmailView(false);
                break;
            case 'email-signup':
                newContent = renderEmailView(true);
                break;
            case 'phone':
                newContent = renderPhoneView();
                break;
            case 'phone-verify':
                newContent = renderPhoneVerifyView();
                break;
            default:
                newContent = renderMainView();
        }

        currentOverlay.innerHTML = newContent;

        // Animate in
        const newCard = currentOverlay.querySelector('.auth-prompt-card');
        requestAnimationFrame(() => {
            newCard.style.opacity = '1';
            newCard.style.transform = 'translateY(0)';
        });

        // Focus first input
        const input = currentOverlay.querySelector('input');
        if (input) {
            setTimeout(() => input.focus(), 100);
        }
    }, 200);
}

function showError(message) {
    const errorEl = currentOverlay?.querySelector('#auth-error');
    if (errorEl) {
        errorEl.textContent = message;
        errorEl.style.display = 'block';
    }
}

function clearError() {
    const errorEl = currentOverlay?.querySelector('#auth-error');
    if (errorEl) {
        errorEl.textContent = '';
        errorEl.style.display = 'none';
    }
}

function setLoading(loading) {
    const btn = currentOverlay?.querySelector('.auth-btn-primary, #auth-google');
    if (btn) {
        btn.disabled = loading;
        btn.style.opacity = loading ? '0.7' : '1';
    }
}

// ─────────────────────────────────────────────────────────────────────────
// AUTH HANDLERS
// ─────────────────────────────────────────────────────────────────────────

async function handleGoogleSignIn() {
    setLoading(true);
    clearError();

    const result = await signInWithGoogle();

    if (result.success) {
        handleSuccess(result.user);
    } else {
        showError(result.error);
        setLoading(false);
    }
}

async function handleEmailSubmit(form) {
    const email = form.querySelector('#auth-email-input').value;
    const password = form.querySelector('#auth-password').value;
    const nameInput = form.querySelector('#auth-name');
    const name = nameInput?.value;

    setLoading(true);
    clearError();

    const isSignUp = currentMode === 'email-signup';

    const result = isSignUp
        ? await createAccountWithEmail(email, password, name)
        : await signInWithEmail(email, password);

    if (result.success) {
        handleSuccess(result.user);
    } else {
        showError(result.error);
        setLoading(false);
    }
}

async function handlePhoneSend(form) {
    const phone = form.querySelector('#auth-phone-input').value;

    setLoading(true);
    clearError();

    const result = await sendPhoneVerification(phone, 'auth-send-code');

    if (result.success) {
        switchView('phone-verify');
    } else {
        showError(result.error);
        setLoading(false);
    }
}

async function handlePhoneVerify(form) {
    const code = form.querySelector('#auth-code-input').value;

    setLoading(true);
    clearError();

    const result = await verifyPhoneCode(code);

    if (result.success) {
        handleSuccess(result.user);
    } else {
        showError(result.error);
        setLoading(false);
    }
}

function handleSuccess(user) {
    // Execute pending action if any
    if (pendingAction?.callback) {
        pendingAction.callback();
    }

    hideAuthPrompt();

    // Show welcome toast
    showToast(`Welcome${user.displayName ? `, ${user.displayName.split(' ')[0]}` : ''}!`);
}

function showToast(message) {
    const existing = document.querySelector('.toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => {
        toast.style.opacity = '0';
        toast.style.transform = 'translateX(-50%) translateY(20px)';
        setTimeout(() => toast.remove(), 300);
    }, 2500);
}

// ─────────────────────────────────────────────────────────────────────────
// EXPORT
// ─────────────────────────────────────────────────────────────────────────

export default { showAuthPrompt, hideAuthPrompt };
